<template>
  <q-dialog
    v-model="shown"
    :persistent="!!paymentConnectorPaymentInProgress"
    data-test-id="refund-dialog"
  >
    <q-card
      v-if="order"
      style="min-width: 50%;"
    >
      <q-card-section class="row items-center q-mx-xs">
        <div class="text-h6">
          {{ order.state === 'open_tab' ? 'Void Items' : 'Refund Order' }}
        </div>
        <q-space />
        <q-btn
          v-if="!paymentConnectorPaymentInProgress"
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section>
        <model-table
          :collection="order.orderItems"
          selection="multiple"
          data-test-id="refund-dialog-order-items-table"
          :columns="[
            { field: 'id', hide: $consumer },
            { field: 'type', component: OrderItemTypeCell, hide: $consumer },
            { field: 'state', component: OrderStateCell, hide: $consumer },
            { field: 'description', maxWidth: '250px' },
            { field: 'total', component: 'money' },
            { field: 'cannotVoidReason' },
          ]"
          :cell-class="props => classesForRow(props.row)"
          :show-add="false"
          :show-edit="false"
          row-key="id"
          :initial-selection="selectedOrderItems"
          :filter-by-data-array="selectableOrderItems"
          @selected-input="selectedValues => selectedOrderItems = selectedValues"
        />
        <model-table
          v-if="($consumer || !allowCollectedItemsRefund) && nonRefundableOrderItems.length"
          title="Non-Refundable Items"
          :collection="order.orderItems"
          :columns="[
            { field: 'description', maxWidth: '250px' },
            { field: 'total', component: 'money' },
            { field: 'nonRefundableReason', name: 'Reason' },
          ]"
          :filter-by-data-array="nonRefundableOrderItems"
          :cell-class="props => classesForNonRefundableRow(props.row)"
          :show-add="false"
          :show-edit="false"
          readonly
        />
        <div
          v-if="amountRemaining > 0"
          class="row justify-end q-gutter-sm q-mt-xs"
        >
          <div class="col-auto">
            Refund Amount
          </div>
          <i18n-n
            class="col-auto"
            tag="span"
            :value="amountRemaining"
            format="currency"
            scope="global"
          />
        </div>
      </q-card-section>
      <q-card-section>
        <q-toggle
          v-if="!$consumer && $isPermitted($permissions.Customer.ManagePayments)"
          :model-value="allowCollectedItemsRefund"
          label="Allow Refund of Collected Items"
          color="primary"
          :text-color="buttonTextColor"
          data-test-id="refund-dialog-allow-collected-ticket-refund-toggle"
          @update:model-value="handleCollectedItemsRefundToggle"
          @click.stop=""
        />
      </q-card-section>
      <q-card-actions
        v-if="$consumer && !currentSite.allowConsumerCreditCardRefunds"
        class="row justify-center"
      >
        <q-btn
          class="col"
          size="xl"
          color="primary"
          :text-color="buttonTextColor"
          :disable="validSelection.length === 0"
          :loading="refunding"
          @click="refund(true)"
        >
          {{ refundAmount ? 'Refund' : 'Void' }}
          <i18n-n
            v-if="refundAmount"
            class="q-ml-xs"
            tag="span"
            :value="refundAmount"
            format="currency"
            scope="global"
          />&nbsp;{{ refundAmount ? 'to Credit' : '' }}
        </q-btn>
      </q-card-actions>
      <q-card-actions
        v-else
        vertical
      >
        <div class="q-ml-md text-h7">
          Select Refund Method
        </div>
        <div class="row q-gutter-sm q-mx-sm q-my-xs items-start">
          <div
            v-for="refundOption in refundOptions"
            :key="refundOption.id"
            class="col-auto column items-center"
            data-test-id="refund-dialog-refund-options"
          >
            <q-btn
              :color="refundOption.color"
              :disabled="refundOption.disabled || !amountRemaining && !refundOption.amount"
              :outline="!isRefundOptionSelected(refundOption)"
              @click="editAmount(refundOption.id)"
            >
              {{ refundOption.label || $_.startCase(refundOption.type) }}
              <q-chip
                v-if="refundOption.amount"
                dense
                size="sm"
              >
                <i18n-n
                  :value="refundOption.amount"
                  format="currency"
                  scope="global"
                />
              </q-chip>
            </q-btn>
            <div
              v-if="refundOption.maxAmount"
              class="text-overline text-grey-8"
            >
              Up to
              <i18n-n
                :value="refundOption.maxAmount"
                format="currency"
                scope="global"
              />
            </div>
            <q-tooltip v-if="refundOption.tooltip">
              {{ refundOption.tooltip }}
            </q-tooltip>
          </div>
        </div>
        <div class="row">
          <q-space />
          <q-btn
            color="primary"
            :text-color="buttonTextColor"
            :disable="validSelection.length === 0 || !!amountRemaining"
            :loading="refunding"
            data-test-id="refund-dialog-refund-button"
            @click="refund"
          >
            {{ refundAmount ? 'Refund' : 'Void' }}
            <i18n-n
              v-if="refundAmount"
              class="q-ml-xs"
              tag="span"
              :value="refundAmount"
              format="currency"
              scope="global"
            />&nbsp;({{ validSelection.length }} item{{ validSelection.length === 1 ? '' : 's' }})
          </q-btn>
        </div>
      </q-card-actions>
    </q-card>
    <q-dialog v-model="showEditAmount">
      <q-card :style="{ 'width': '450px', 'max-width': '95%' }">
        <q-card-section
          v-if="refundType"
          class="text-h5"
        >
          Amount to Refund to {{ refundType.label || $_.startCase(refundType.type) }}
        </q-card-section>

        <q-card-section class="row justify-evenly items-center">
          <q-input
            v-if="refundType"
            ref="refundAmount"
            v-model.number="refundType.amount"
            label="Amount"
            :prefix="currentCircuit.localeCurrencySymbol"
            type="number"
            step="0.01"
            :rules="[
              val => val > 0 || 'Must be positive value',
              val => val !== null && val !== '' || 'Cannot be blank',
              val => amountRemaining >= 0 || 'Cannot exceed amount owed',
              val => val <= !refundType.maxAmount || val <= refundType.maxAmount || 'Cannot exceed payment'
            ]"
            autofocus
            @keyup.enter="showEditAmount = false"
          />
        </q-card-section>

        <q-card-actions class="row justify-end q-ma-sm">
          <q-btn
            v-close-popup
            label="Done"
            color="primary"
            :text-color="buttonTextColor"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <interac-refund-dialog
      ref="interacRefundDialog"
      :order="order"
      @refund-success="handleInteracRefundSuccess"
    />
  </q-dialog>
</template>

<script>
import { Dialog } from 'quasar'
import { TYPES as BOOKING_FEE_TYPES } from 'src/models/booking-fee-type/BookingFeeType'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import { PAYMENT_STATES, PAYMENT_TYPES } from 'src/models/payment/Payment'
import ModelTable from 'src/modules/admin/components/ModelTable'
import OrderItemTypeCell from 'src/modules/admin/order/components/OrderItemTypeCell'
import OrderStateCell from 'src/modules/admin/order/components/OrderStateCell'
import InteracRefundDialog from 'src/modules/admin/payment/components/InteracRefundDialog'
const PAYMENT_CONNECTOR_PAYMENT_STARTED_AT_KEY = 'paymentConnectorStartedTimestamp'

const PAYMENT_CONNECTOR_IN_PROGRESS_KEY = 'paymentConnectorInProgressOrder'

export default {
  name: 'RefundDialog',
  components: {
    ModelTable,
    InteracRefundDialog
  },
  configuration: ['currentCircuit', 'currentSite', 'paymentTypes'],
  props: {
    modelValue: {
      type: Boolean,
      required: false
    }
  },
  emits: ['update:model-value', 'show-payment-dialog', 'refund-success'],
  data () {
    return {
      order: null,
      shown: false,
      selectedOrderItems: [],
      refundOptions: [],
      refunding: false,
      showEditAmount: false,
      editAmountId: null,
      amount: 0,
      allowCollectedItemsRefund: false
    }
  },
  computed: {
    refundAmount () {
      return _(this.validSelection).filter('paid').map('total').sum()
    },
    amountRemaining () {
      return (Math.round((this.refundAmount - _(this.refundOptions).map('amount').sum()) * 100) / 100)
    },
    refundType () {
      return _.find(this.refundOptions, { id: this.editAmountId })
    },
    validSelection () {
      return _.filter(this.selectedOrderItems, { placed: true, canVoid: true })
    },
    paymentConnectorPaymentInProgress () {
      return _.find(this.order?.payments?.data, payment => payment.state === PAYMENT_STATES.DRAFT && payment.nonStripeCardPayment && payment.isRemote)
    },
    selectableOrderItems () {
      const selectableOrderItems = _.filter(this.order.orderItems.data, orderItem => {
        if (this.$consumer && !orderItem.consumerRefundable.isRefundable) return false

        if (!this.$consumer && orderItem.type === ORDER_ITEM_TYPES.TICKET && !!orderItem.collectedAt && !this.allowCollectedItemsRefund) return false

        if (orderItem.type !== ORDER_ITEM_TYPES.BOOKING_FEE) return true

        if (orderItem.subtype === BOOKING_FEE_TYPES.ORDER) return true

        return _.some(this.selectedOrderItems, selectedOrderItem => selectedOrderItem.orderItemBookingFee?.id === orderItem.id)
      })

      return selectableOrderItems
    },
    nonRefundableOrderItems () {
      if (this.$consumer) {
        return _.filter(this.order.orderItems.data, orderItem => !orderItem.consumerRefundable.isRefundable && orderItem.type !== ORDER_ITEM_TYPES.BOOKING_FEE)
      } else if (!this.allowCollectedItemsRefund) {
        return _.filter(this.order.orderItems.data, orderItem => {
          if (orderItem.type === ORDER_ITEM_TYPES.BOOKING_FEE) return false
          if (orderItem.collectedAt) return true
          return false
        })
      }

      return []
    },
    buttonTextColor () {
      return this.$isColorDark(this.currentSite.primaryColor) ? 'white' : 'black'
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    selectedOrderItems () {
      const validSelectedItems = _.intersection(this.selectedOrderItems, this.selectableOrderItems)
      if (_.difference(this.selectedOrderItems, validSelectedItems).length) {
        this.selectedOrderItems = _.intersection(this.selectedOrderItems, validSelectedItems)
      }
    },
    shown () {
      this.$emit('update:model-value', this.shown)
      this.setRefundOptions()

      this.initializeSelectedOrderItems()
    }
  },
  created () {
    _.extend(this, {
      OrderItemTypeCell,
      OrderStateCell,
      PAYMENT_STATES,
      PAYMENT_TYPES
    })
  },
  methods: {
    show (order, initialSelectedIds = null) {
      this.order = order
      this.initialSelectedIds = initialSelectedIds
      this.shown = true
    },
    initializeSelectedOrderItems () {
      let defaultSelectedOrderItems = this.order.orderItems.data

      defaultSelectedOrderItems = _.filter(defaultSelectedOrderItems, orderItem => {
        // All standard order items are refunded by default
        if (orderItem.type !== ORDER_ITEM_TYPES.BOOKING_FEE) return true
        // Filter out non-refundable by default booking fees
        if (orderItem.bookingFeeType.refundable && orderItem.bookingFeeType.refundByDefault) return true

        return false
      })

      if (this.initialSelectedIds) {
        this.selectedOrderItems = [..._.filter(defaultSelectedOrderItems, orderItem => _.includes(this.initialSelectedIds, orderItem.id))]
      } else {
        this.selectedOrderItems = [..._.filter(defaultSelectedOrderItems, { placed: true })]
      }
    },
    isRefundOptionSelected (refundOption) {
      return refundOption.amount > 0
    },
    clearAmounts () {
      this.refundOptions = _.map(this.refundOptions, option => ({ ...option, amount: 0 }))
    },
    setRefundOptions () {
      this.refundOptions = []
      const allowCardRefunds = this.$consumer ? this.currentSite.allowConsumerCreditCardRefunds : true

      _.each(this.order.payments.data, payment => {
        // Remove any draft payments in case of refund failure to avoid duplications.
        if (payment.state === PAYMENT_STATES.DRAFT) {
          this.order.payments.remove(payment)
          return
        }

        if (payment.state !== PAYMENT_STATES.COMPLETED || payment.amount < 0) return

        if (payment.type === PAYMENT_TYPES.CARD && allowCardRefunds) {
          if (this.$consumer && (_.isNil(payment.stripePaymentIntentId) || payment.isInteracPayment)) return
          // Support multiple partial refunds
          const cardPaymentMaxAmount = _(this.order.payments.data).filter({ stripePaymentIntentId: payment.stripePaymentIntentId, state: 'completed' }).map('amount').sum()

          const consumerMaxAmount = _.min([this.order.consumerRefundableAmount, cardPaymentMaxAmount])

          const maxAmount = this.$consumer ? consumerMaxAmount : cardPaymentMaxAmount

          const cardLabel = payment.cardType === 'link' ? 'Link' : `Credit Card ...${payment.lastFour}`
          const label = `${payment.paymentConnectorCanVoid ? 'Refund ' : ''}${cardLabel}`
          // Add Stripe Refund Option
          this.refundOptions.push({
            id: payment.id,
            type: PAYMENT_TYPES.CARD,
            subtype: payment.subtype,
            label,
            color: 'primary',
            isInteracPayment: payment.isInteracPayment,
            stripeChargeId: payment.stripeChargeId,
            stripePaymentIntentId: payment.stripePaymentIntentId,
            lastFour: payment.lastFour,
            cardType: payment.cardType,
            nameOnCard: payment.nameOnCard,
            refundFromPaymentId: payment.id,
            amount: 0,
            maxAmount,
            paymentConnectorVoid: false
          })

          // Add Non-Stripe Void Option (Special Refund for select Payment Connectors)
          if (payment.paymentConnectorCanVoid && !this.$consumer) {
            this.refundOptions.push({
              id: `V${payment.id}`,
              type: PAYMENT_TYPES.CARD,
              subtype: payment.subtype,
              label: 'Void Credit Card ...' + payment.lastFour,
              color: 'negative',
              stripePaymentIntentId: payment.stripePaymentIntentId,
              lastFour: payment.lastFour,
              cardType: payment.cardType,
              nameOnCard: payment.nameOnCard,
              refundFromPaymentId: payment.id,
              amount: 0,
              maxAmount,
              paymentConnectorVoid: payment.paymentConnectorCanVoid
            })
          }
        }

        // Add Gift Card Refund Option
        if (!this.$consumer && payment.type === PAYMENT_TYPES.GIFT_CARD) {
          this.refundOptions.push({
            id: payment.id,
            type: PAYMENT_TYPES.GIFT_CARD,
            label: 'Gift Card',
            color: 'accent',
            giftCard: payment.giftCard,
            amount: 0
          })
        }

        // Add Custom Payment Type Refund Option
        if (!this.$consumer && payment.paymentTypeId) {
          const resolvedPaymentType = _.find(this.paymentTypes.data, { id: payment.paymentTypeId })
          if (!resolvedPaymentType) {
            this.refundOptions.push({
              id: payment.id,
              paymentTypeId: payment.paymentTypeId,
              label: payment.subtype + ' (Not Found)',
              color: 'warning',
              disabled: true,
              amount: 0
            })
          } else {
            this.refundOptions.push({
              id: payment.id,
              paymentTypeId: resolvedPaymentType.id,
              label: resolvedPaymentType.name,
              color: 'warning',
              disabled: !resolvedPaymentType.enabled,
              amount: 0
            })
          }
        }
      })

      // Add Cash Refund Option
      // For orders that were a credit, don't allow cash, only taking credit back from user
      if (this.order.total > 0 && this.currentSite.allowCashPayments && !this.$consumer) {
        this.refundOptions.push({
          id: PAYMENT_TYPES.CASH,
          type: PAYMENT_TYPES.CASH,
          amount: 0,
          color: 'green',
          disabled: !this.$register,
          tooltip: !this.$register ? 'Cash refunds require register' : null
        })
      }

      // Add User Credit Refund Option
      this.refundOptions.push({
        id: PAYMENT_TYPES.CREDIT,
        type: PAYMENT_TYPES.CREDIT,
        label: 'User Credit',
        amount: 0,
        color: 'positive',
        disabled: !this.order.user,
        tooltip: !this.order.user ? 'Credit refunds require order owner' : null
      })
    },
    classesForRow (orderItem) {
      if (orderItem.state === 'placed' || orderItem.state === 'paid') {
        return []
      } else if (orderItem.state === 'voided') {
        return ['tr-bg-grey-5', 'bg-grey-5']
      } else if (orderItem.state === 'draft') {
        return ['tr-bg-grey-4', 'bg-grey-4']
      } else {
        throw new Error('Unhandled Condition')
      }
    },
    classesForNonRefundableRow () {
      return ['tr-bg-grey-4', 'bg-grey-4']
    },
    async editAmount (id) {
      this.editAmountId = id
      // If this is the first time, just add it. If there is already an amount, edit it
      if (this.refundType.amount) {
        this.showEditAmount = true
        await this.$nextTick()
        this.$refs.refundAmount.select()
      } else {
        this.refundType.amount = Math.round((!_.isNil(this.refundType.maxAmount) ? _.min([this.amountRemaining, this.refundType.maxAmount]) : this.amountRemaining) * 100) / 100
      }
    },
    handleInteracRefundSuccess (refundOption) {
      this.refunding = true
      this.refundOptions = _.filter(this.refundOptions, option => option.id !== refundOption.id)
      this.refund()
    },
    setCredit () {
      this.editAmountId = PAYMENT_TYPES.CREDIT
      this.refundType.amount = 0
      this.editAmount(PAYMENT_TYPES.CREDIT)
    },
    async refund (asCredit = false) {
      this.refunding = true

      if (asCredit) {
        this.setCredit()
      }

      const cashChangeDue = _(this.refundOptions).filter({ type: PAYMENT_TYPES.CASH }).map('amount').sum()
      const paymentsToRefund = _.filter(this.refundOptions, option => option.amount !== 0)
      const interacPayments = _.filter(paymentsToRefund, option => option.isInteracPayment)

      if (!_.isEmpty(interacPayments)) {
        // Process interac payments first
        // Interac payments are processed using the stripe terminal, so we need to process them first
        const interacPayment = _.first(interacPayments)
        this.$refs.interacRefundDialog.show(interacPayment, interacPayment.amount)
        this.refunding = false
        return
      }

      const voidSuccess = await this.order.void(_.map(this.validSelection, 'id'), _.filter(this.refundOptions, option => !_.isNil(option.amount) && option.amount !== 0))

      if (!voidSuccess) {
        this.refunding = false
        return
      }

      if (cashChangeDue > 0) {
        Dialog.create({
          title: 'Change',
          message: `Change Due: ${this.$i18n.n(cashChangeDue, 'currency')}`
        })
      }

      if (this.paymentConnectorPaymentInProgress) {
        localStorage.setItem(PAYMENT_CONNECTOR_IN_PROGRESS_KEY, this.order.id)
        localStorage.setItem(PAYMENT_CONNECTOR_PAYMENT_STARTED_AT_KEY, Date.now())
        this.$emit('show-payment-dialog', this.order)
      } else {
        this.refunding = false
        this.$emit('refund-success')
        this.allowCollectedItemsRefund = false
        this.shown = false
      }
    },
    handleCollectedItemsRefundToggle (newValue) {
      if (newValue) {
        Dialog.create({
          title: 'Confirm Refund of Collected Items',
          message: 'Are you sure you want to allow refunds of collected items?',
          ok: {
            label: 'Ok',
            flat: true,
            'data-test-id': 'confirm-collected-items-refund-ok'
          },
          cancel: {
            label: 'Cancel',
            flat: true,
            'data-test-id': 'confirm-collected-items-refund-cancel'
          },
          persistent: true
        }).onOk(() => {
          this.allowCollectedItemsRefund = true
        })
      } else {
        this.allowCollectedItemsRefund = false
        this.selectedOrderItems = _.intersection(this.selectedOrderItems, this.selectableOrderItems)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.tr-bg-grey-4) {
  background: #DDD;
}
:deep(.tr-bg-grey-5) {
  background: #BDBDBD;
}
</style>
